import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MessageCircle, CheckCircle, AlertCircle, Youtube, Play } from 'lucide-react';
import { Button } from '../ui/button';
import { createSectionTracker, trackEvent, type SemaforoColor } from '../../services/funnelTracker';

interface PostFormCTAProps {
  semaforo: {
    dolor: SemaforoColor;
    capacidad: SemaforoColor;
    timing: SemaforoColor;
    inversion: SemaforoColor;
    score: number;
  };
}

const CALENDLY_URL = 'https://calendly.com/lucianoagustinargolo/sesion_informativa';
const LINKEDIN_URL = 'https://www.linkedin.com/in/luciano-argolo/';
const YOUTUBE_URL = 'https://www.youtube.com/@lucianoargolo1608';
const TIKTOK_URL = 'https://www.tiktok.com/@lucianoargolo0';

export default function PostFormCTA({ semaforo }: PostFormCTAProps) {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const tracker = createSectionTracker('post_form');
    if (sectionRef.current) tracker.ref(sectionRef.current);
    return () => tracker.cleanup();

    // Track which result screen was shown
  }, []);

  useEffect(() => {
    trackEvent('result_shown', {
      score: semaforo.score,
      variant: semaforo.score >= 2 ? 'qualified' : semaforo.score >= 1 ? 'mixed' : 'redirect_free',
    });
  }, [semaforo]);

  const handleCalendarClick = () => {
    trackEvent('calendar_click', { score: semaforo.score });
    window.open(CALENDLY_URL, '_blank');
  };

  const handleLinkedinClick = () => {
    trackEvent('linkedin_click', { score: semaforo.score });
    window.open(LINKEDIN_URL, '_blank');
  };

  const handleYoutubeClick = () => {
    trackEvent('youtube_click', { score: semaforo.score });
    window.open(YOUTUBE_URL, '_blank');
  };

  const handleTiktokClick = () => {
    trackEvent('tiktok_click', { score: semaforo.score });
    window.open(TIKTOK_URL, '_blank');
  };

  // ============================================
  // QUALIFIED (2+ greens)
  // ============================================
  if (semaforo.score >= 2) {
    return (
      <section ref={sectionRef} className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-green-600/20 via-green-500/10 to-blue-600/20 border border-green-500/40 rounded-3xl p-8 sm:p-12 text-center shadow-2xl"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            >
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
            </motion.div>

            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              Perfecto, estás listo para arrancar
            </h2>
            <p className="text-gray-300 text-base sm:text-lg mb-8 max-w-lg mx-auto">
              Tu situación y tu presupuesto encajan con las mentorías.
              El siguiente paso es una <span className="text-green-400 font-semibold">llamada gratuita de 15 min</span> donde
              vemos tu caso, armamos un plan y te explico cómo funciona.
            </p>

            <div className="space-y-4">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-8 py-5 text-base sm:text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                onClick={handleCalendarClick}
              >
                <Calendar className="mr-2 w-5 h-5" />
                Agendá tu llamada gratuita
              </Button>

              <Button
                variant="outline"
                className="w-full sm:w-auto border-green-500/40 text-green-400 hover:bg-green-500/10 px-6 py-3 rounded-full transition-all"
                onClick={handleLinkedinClick}
              >
                <MessageCircle className="mr-2 w-4 h-4" />
                Escribime en LinkedIn
              </Button>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-8 flex items-center justify-center gap-2 text-sm text-yellow-400"
            >
              <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
              Tengo cupos limitados para asegurar el seguimiento real de mis alumnos
            </motion.div>
          </motion.div>
        </div>
      </section>
    );
  }

  // ============================================
  // MIXED (1 green or mixed yellows)
  // ============================================
  if (semaforo.score >= 1) {
    return (
      <section ref={sectionRef} className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-blue-600/20 via-purple-600/10 to-blue-700/20 border border-blue-500/30 rounded-3xl p-8 sm:p-12 text-center shadow-2xl"
          >
            <AlertCircle className="w-14 h-14 text-blue-400 mx-auto mb-6" />

            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              Hay camino, pero armemos un plan primero
            </h2>
            <p className="text-gray-300 text-base sm:text-lg mb-8 max-w-lg mx-auto">
              Tu perfil tiene potencial pero hay algunos puntos que podemos resolver juntos.
              Mientras tanto, tengo contenido gratuito en YouTube y TikTok donde comparto
              todo lo que necesitás para arrancar.
            </p>

            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-8 py-5 text-base sm:text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  onClick={handleYoutubeClick}
                >
                  <Youtube className="mr-2 w-5 h-5" />
                  YouTube
                </Button>
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-gray-800 hover:bg-gray-700 text-white px-8 py-5 text-base sm:text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  onClick={handleTiktokClick}
                >
                  <Play className="mr-2 w-5 h-5" />
                  TikTok
                </Button>
              </div>

              <div className="text-gray-500 text-sm">o si querés charlar de todas formas</div>

              <Button
                variant="outline"
                className="w-full sm:w-auto border-blue-500/40 text-blue-400 hover:bg-blue-500/10 px-6 py-3 rounded-full transition-all"
                onClick={handleCalendarClick}
              >
                <Calendar className="mr-2 w-4 h-4" />
                Agendá una llamada igual (gratis)
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  // ============================================
  // NOT QUALIFIED (0 greens)
  // ============================================
  return (
    <section ref={sectionRef} className="py-16 sm:py-24 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-3xl p-8 sm:p-12 text-center shadow-2xl"
        >
          <Youtube className="w-14 h-14 text-gray-400 mx-auto mb-6" />

          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            Arrancá por acá, es gratis
          </h2>
          <p className="text-gray-300 text-base sm:text-lg mb-8 max-w-lg mx-auto">
            Por ahora la mentoría paga puede no ser el mejor siguiente paso para vos.
            Pero en mi canal de YouTube y TikTok comparto contenido gratuito de SQL, Python
            y Data Engineering para que vayas construyendo las bases.
          </p>

          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                size="lg"
                className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-8 py-5 text-base sm:text-lg rounded-full shadow-lg transition-all duration-300 transform hover:scale-105"
                onClick={handleYoutubeClick}
              >
                <Youtube className="mr-2 w-5 h-5" />
                YouTube
              </Button>
              <Button
                size="lg"
                className="w-full sm:w-auto bg-gray-800 hover:bg-gray-700 text-white px-8 py-5 text-base sm:text-lg rounded-full shadow-lg transition-all duration-300 transform hover:scale-105"
                onClick={handleTiktokClick}
              >
                <Play className="mr-2 w-5 h-5" />
                TikTok
              </Button>
            </div>

            <div>
              <button
                onClick={handleLinkedinClick}
                className="text-gray-400 hover:text-white text-sm underline transition-colors"
              >
                O escribime en LinkedIn si tenés dudas
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
